
import moment from 'moment';
import React, { useState } from 'react';
import { TextInput, NavigationWrapper, PercentInput, AddressInput, DateInput, CurrencyInput, CheckboxHeaderCell, CheckboxCell, CheckboxPrimaryCell } from './cells';
import { size } from 'styled-system';
export default function useColumns({
  group,
  handleChange,
  selectedRows,
  setSelectedRows,
  allRowIndices,
  isAllSelected,
  isFieldMissing,
  navigation,
}) {
  const wrapWithNavigation = (CellComponent, props, rowIndex, colIndex) => (
    <NavigationWrapper
      rowIndex={rowIndex}
      colIndex={colIndex}
      navigation={navigation}
    >
      <CellComponent {...props} />
    </NavigationWrapper>
  );

  const checkboxColumn = {
    id: 'select',
    header: () => (
      <CheckboxHeaderCell setSelectedRows={setSelectedRows} isAllSelected={isAllSelected} allRowIndices={allRowIndices} />
    ),
    cell: ({ row }) => {
      const idx = row.index;
      const isNew = row.original._new || false;
      const isPrimary = group === 'primary' && idx === 0;
      if (isPrimary) {
        return <CheckboxPrimaryCell />
      }
      return (
        <CheckboxCell setSelectedRows={setSelectedRows} idx={idx} selectedRows={selectedRows} disabled={isNew} />
      );
    },
    size: 40,
    enableResizing: false,
  };
  const basicColumns = [
    {
      header: 'Address',
      accessorKey: 'address',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const columnIndex = 'address';
        const fieldName = 'address';
        const colIndex = 1; // Вторая колонка
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);
        return wrapWithNavigation(
          AddressInput,
          {
            value: getValue() || '',
            onChange: (value) => handleChange(value, 'address', row.original),
            placeholder: "Enter address...",
            disabled: false,
            isActive: true,
            rowIndex,
            columnIndex: 'address',
            isMissing
          },
          rowIndex,
          colIndex
        );
      },
      minSize: 150,
      size: 200,
    },
    {
      header: 'Owner on Title',
      accessorKey: 'titleName',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const columnIndex = 'titleName';
        const fieldName = 'titleName';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);
        return (
          <TextInput
            value={getValue()}
            type='text'
            onChange={(value) => {
              handleChange(value, 'title_name', row.original);
            }}
            initialValues={row.original}
            isMissing={isMissing}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
          />
        );
      },
      minSize: 150,
    },
    {
      header: '% Ownership',
      accessorKey: 'ownershipPercent',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const columnIndex = 'ownershipPercent';
        const fieldName = 'ownershipPercent';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);
        return (
          <PercentInput
            value={getValue()}
            type='text'
            onChange={(value) => {
              handleChange(value, 'ownership_percent', row.original);
            }}
            isMissing={isMissing}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
          />
        );
      },
      size: 120,
    },
    {
      header: 'Purchase Date',
      accessorKey: 'purchaseDate',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const columnIndex = 'purchaseDate';
        const fieldName = 'purchaseDate';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);
        const date = getValue();
        return (
          <DateInput
            value={date ? moment(date).format('MM/DD/YYYY') : ''}
            onChange={(value, isValid) => {
              if (isValid) {
                handleChange(value, 'purchaseDate', row.original, isValid);
              }
            }}
            isMissing={isMissing}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
          />
        );
      },
      size: 140,
    },
  ];

  const sharedColumns = [
    {
      header: 'Property Value',
      accessorKey: 'currentMarketValue',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const columnIndex = 'currentMarketValue';
        const fieldName = 'currentMarketValue';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);
        return (
          <CurrencyInput
            value={getValue()}
            className='right'
            onChange={(value) => {
              handleChange(value, 'current_market_value', row.original);

            }}
            isMissing={isMissing}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
          />
        );
      },
      size: 140,
    },
    {
      header: 'Lender',
      accessorKey: 'lenderName',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const columnIndex = 'lenderName';
        const fieldName = 'lenderName';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);
        return (
          <TextInput
            value={getValue()}
            type='text'
            className='left'
            onChange={(value) => {
              handleChange(value, 'lender_name', row.original);

            }}
            isMissing={isMissing}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
          />
        );
      },
      size: 140,
    },
    {
      header: 'Original Mortgage Balance',
      accessorKey: 'totalMortgageAmount',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const columnIndex = 'totalMortgageAmount';
        const fieldName = 'totalMortgageAmount';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);
        return (
          <CurrencyInput
            value={getValue()}
            onChange={(value) => {
              handleChange(value, 'total_mortgage_amount', row.original);
            }}
            isMissing={isMissing}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
          />
        );
      },
      size: 200,
    },
  ];

  const appFixNFlipsColumns = [
    ...basicColumns,
    {
      header: 'Purchase Price',
      accessorKey: 'purchasePrice',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const columnIndex = 'purchasePrice';
        const fieldKey = 'purchasePrice';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldKey);
        return (
          <CurrencyInput
            value={getValue()}
            className='right'
            onChange={(value) => {
              handleChange(value, 'purchase_price', row.original);
            }}
            isMissing={isMissing}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
          />
        );
      },
      size: 150,
    },
    {
      header: 'Rehab Budget',
      accessorKey: 'rehabBudget',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const columnIndex = 'rehabBudget';
        const fieldKey = 'rehabBudget';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldKey);
        return (
          <CurrencyInput
            value={getValue()}
            className='right'
            onChange={(value) => {
              handleChange(value, 'rehab_budget', row.original);
            }}
            isMissing={isMissing}

            rowIndex={rowIndex}
            columnIndex={columnIndex}
          />
        );
      },
      size: 150,
    },
    {
      header: 'Sale Price',
      accessorKey: 'salePrice',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const columnIndex = 'salePrice';
        const fieldKey = 'salePrice';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldKey);
        return (
          <CurrencyInput
            value={getValue()}
            className='right'
            onChange={(value) => {
              handleChange(value, 'sale_price', row.original);
            }}
            isMissing={isMissing}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
          />
        );
      },
      size: 150,
    },
    {
      header: 'Date Sold',
      accessorKey: 'saleDate',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const columnIndex = 'saleDate';
        const fieldKey = 'saleDate';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldKey);
        return (
          <DateInput
            value={getValue() ? moment(getValue()).format('MM/DD/YYYY') : ''}
            onChange={(value, isValid) => {
              handleChange(value, 'sale_date', row.original, isValid);
            }}
            isMissing={isMissing}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
          />
        );
      },
      size: 180,
    },
  ];

  const appInvestmentPropertiesOwnedColumns = [
    ...basicColumns,
    ...sharedColumns,
    {
      header: 'Mortgage Payment',
      accessorKey: 'mortgagePayment',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const columnIndex = 'mortgagePayment';
        const fieldName = 'mortgagePayment';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);
        return (
          <CurrencyInput
            value={getValue()}
            className='right'
            onChange={(value) => {
              handleChange(value, 'mortgage_payment', row.original);
            }}
            isMissing={isMissing}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
          />
        );
      },
      size: 200,
    },
    {
      header: 'Actual Rent',
      accessorKey: 'rentAmount',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const columnIndex = 'rentAmount';
        const fieldName = 'rentAmount';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);
        return (
          <CurrencyInput
            value={getValue()}
            className='right'
            onChange={(value) => {
              handleChange(value, 'rent_amount', row.original);
            }}
            isMissing={isMissing}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
          />
        );
      },
      size: 200,
    },
    {
      header: 'Market Rent',
      accessorKey: 'marketRent',
      cell: ({ row, getValue }) => {
        const rowIndex = row.index;
        const columnIndex = 'marketRent';
        const fieldName = 'marketRent';
        const isMissing = isFieldMissing && isFieldMissing(row.original, fieldName);
        return (
          <CurrencyInput
            value={getValue()}
            className='right'
            onChange={(value) => {
              handleChange(value, 'market_rent', row.original);
            }}
            isMissing={isMissing}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
          />
        );
      },
      size: 200,
    },
  ];

  const appPrimaryResidenceAndSecondHomesColumns = [
    ...basicColumns,
    ...sharedColumns,
  ];


  if (group === 'bridge') {
    return [checkboxColumn, ...appFixNFlipsColumns];
  } else if (group === 'rental') {
    return [checkboxColumn, ...appInvestmentPropertiesOwnedColumns];
  } else if (group === 'primary') {
    return [checkboxColumn, ...appPrimaryResidenceAndSecondHomesColumns];
  }
  return [];
}
